import { Link } from "react-router-dom";
import { Mic, Square, History, Loader2, Minimize2, Maximize2 } from "lucide-react";

const fmt = (s) => {
  const total = Math.max(0, Math.floor(s));
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${m}:${ss}`;
};

export default function TopBar({ status, elapsed, micOn, sysOn, onStart, onStop, compactMode, onToggleCompact, mode, onToggleMode }) {
  const isLive = status === "live";
  const isStarting = status === "starting";
  const isSummarizing = status === "summarizing";
  return (
    <header
      data-testid="top-bar"
      className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-50"
    >
      <div className="flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white font-display font-bold text-sm">
            SC
          </span>
          <span className="font-display text-base font-semibold tracking-tight text-slate-900">
            Sales Closer Helper
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 ml-1">
            v0.1
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2" data-testid="status-indicator">
          {isLive ? (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-red-500 sch-pulse" />
              <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">
                Live
              </span>
            </>
          ) : isStarting ? (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">
                Starting…
              </span>
            </>
          ) : isSummarizing ? (
            <>
              <Loader2 className="h-3 w-3 text-slate-500 animate-spin" />
              <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">
                Summarizing
              </span>
            </>
          ) : (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">
                Idle
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className={micOn ? "text-emerald-700" : ""} data-testid="mic-channel-status">
            Closer mic <span className="font-mono">{micOn ? "ON" : "OFF"}</span>
          </span>
          <span className="text-slate-300">|</span>
          <span className={sysOn ? "text-emerald-700" : ""} data-testid="sys-channel-status">
            Prospect tab <span className="font-mono">{sysOn ? "ON" : "OFF"}</span>
          </span>
        </div>

        <div
          className="font-mono text-base text-slate-800 tabular-nums min-w-[64px] text-right"
          data-testid="elapsed-timer"
        >
          {fmt(elapsed)}
        </div>

        {isLive ? (
          <button
            onClick={onStop}
            data-testid="stop-listening-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium transition-colors"
          >
            <Square className="h-4 w-4" /> Stop &amp; Summarize
          </button>
        ) : isSummarizing ? (
          <button
            disabled
            data-testid="summarizing-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-200 text-slate-600 px-4 py-2 text-sm font-medium cursor-not-allowed"
          >
            <Loader2 className="h-4 w-4 animate-spin" /> Summarizing…
          </button>
        ) : (
          <button
            onClick={onStart}
            disabled={isStarting}
            data-testid="start-listening-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Mic className="h-4 w-4" /> Start Listening
          </button>
        )}

        <Link
          to="/sessions"
          data-testid="history-link"
          className="text-slate-500 hover:text-slate-900"
          title="Past calls"
        >
          <History className="h-5 w-5" />
        </Link>

        {onToggleMode && (
          <button
            type="button"
            onClick={onToggleMode}
            data-testid="toggle-mode"
            title={mode === "b2c"
              ? "B2C: Goals → Pain → deeper. Click to switch to B2B."
              : "B2B: Pain → Impact → Goals. Click to switch to B2C."}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-2 py-1.5 text-[10px] font-mono font-semibold text-slate-700 uppercase tracking-wider"
          >
            {mode === "b2c" ? "B2C" : "B2B"}
          </button>
        )}

        {onToggleCompact && (
          <button
            type="button"
            onClick={onToggleCompact}
            data-testid="toggle-compact-mode"
            title={compactMode ? "Switch to full view" : "Switch to compact closer mode"}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700"
          >
            {compactMode ? (
              <>
                <Maximize2 className="h-3.5 w-3.5" /> Full
              </>
            ) : (
              <>
                <Minimize2 className="h-3.5 w-3.5" /> Compact
              </>
            )}
          </button>
        )}
      </div>
    </header>
  );
}
